<template>
  <main class="min-h-[60vh] flex items-center justify-center px-4 py-16">
    <section class="w-full max-w-[640px] rounded-[2.4rem] border border-black/10 bg-white p-8 shadow-sm text-center space-y-4">
      <p class="m-0 text-[1.6rem] font-semibold text-black">
        {{ $t('Processing payment, please wait…') }}
      </p>
      <p v-if="errorMessage" class="m-0 text-[1.3rem] text-red-600">
        {{ errorMessage }}
      </p>
      <p v-else class="m-0 text-[1.3rem] text-[color:var(--gray)]">
        {{ $t('We are confirming your payment. This may take a few seconds.') }}
      </p>
      <button
        v-if="errorMessage"
        type="button"
        class="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-4 py-2 text-[1.3rem] font-semibold text-white transition hover:bg-black/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
        @click="goBack"
      >
        {{ $t('Return to checkout') }}
      </button>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { fetchStripeStatus } from '@/services/checkoutService';
import CartService from '@/services/cartService';
import { useCheckoutStore } from '@/stores/useCheckoutStore';

const MAX_RETRIES = 30;
const RETRY_DELAY_MS = 1000;

export default defineComponent({
  name: 'StripeCallbackView',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const checkoutStore = useCheckoutStore();
    const errorMessage = ref<string | null>(null);
    const attempts = ref(0);
    const paymentIntentId = ref<string | null>(null);
    const tenantUrl = ref<string | null>(null);

    const resolveTenantUrl = () => {
      const cart = CartService.getCart ? CartService.getCart() : null;
      const cartTenantRaw = cart?.sales?.[0]?.tenantUrl ?? null;
      const contextTenantRaw = (checkoutStore.checkoutContext as Record<string, unknown> | null)?.tenantUrl ?? null;
      const cartTenant = typeof cartTenantRaw === 'string' ? cartTenantRaw : null;
      const contextTenant = typeof contextTenantRaw === 'string' ? contextTenantRaw : null;
      tenantUrl.value = cartTenant || contextTenant || null;
    };

    const extractPaymentIntent = (): string | null => {
      const query = route.query;
      let intent: string | null = null;

      const raw = query.payment_intent;
      if (Array.isArray(raw)) {
        intent = raw[0] as string;
      } else if (typeof raw === 'string') {
        intent = raw;
      }

      if (!intent) {
        const secret = query.payment_intent_client_secret;
        let secretValue: string | null = null;
        if (Array.isArray(secret)) {
          secretValue = secret[0] as string;
        } else if (typeof secret === 'string') {
          secretValue = secret;
        }
        if (secretValue) {
          const parts = secretValue.split('_secret');
          if (parts.length > 0) {
            intent = parts[0];
          }
        }
      }

      return intent;
    };

    const handleSuccess = () => {
      CartService.clear();
      checkoutStore.setCheckoutContext(null);
      router.replace({ name: 'BookingSuccess' }).catch(() => {
        router.replace('/checkout/booking-success');
      });
    };

    const handleError = (message: string | null) => {
      errorMessage.value = message || t('We could not confirm your payment. Please try again.');
    };

    const pollStatus = async () => {
      if (!paymentIntentId.value) {
        handleError(t('We could not confirm your payment. Please try again.'));
        return;
      }

      try {
        const response = await fetchStripeStatus(paymentIntentId.value, tenantUrl.value);
        const shouldRetry = response && response.retry === true && attempts.value < MAX_RETRIES;
        const isOk = response && response.ok === true;

        if (isOk) {
          handleSuccess();
          return;
        }

        if (shouldRetry) {
          attempts.value += 1;
          window.setTimeout(pollStatus, RETRY_DELAY_MS);
          return;
        }

        if (response && response.errorMsg) {
          handleError(response.errorMsg);
          return;
        }

        handleError(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : null;
        handleError(message);
      }
    };

    const goBack = () => {
      router.replace({ name: 'CheckoutPrebookSuccess' }).catch(() => {
        router.replace('/checkout');
      });
    };

    onMounted(() => {
      paymentIntentId.value = extractPaymentIntent();
      resolveTenantUrl();
      pollStatus();
    });

    return {
      errorMessage,
      goBack,
    };
  },
});
</script>
